import React, { useCallback, useEffect, useMemo } from 'react';
import { Drawer, Box, Group, Text, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import type { TaskSnapshot } from '../../../stores/types';
import type { PredecessorLinkChange } from '../../../gantt/taskLinkDisplayUtils';
import { useTaskDetailsDraft } from '../../../hooks/useTaskDetailsDraft';
import { TaskDetailsFormBody } from './TaskDetailsFormBody';
import { TaskDetailsFormActions } from './TaskDetailsFormActions';
import { TaskModalBusyOverlay } from './TaskModalBusyOverlay';
import { TrackTaskConfirmControl } from './TrackTaskConfirmControl';
import { shellDrawerClassNames } from '../../../app/shellDrawerClassNames';

const DRAWER_SURFACE = 'var(--app-drawer-surface, var(--mantine-color-gray-0))';

interface TaskDetailsDrawerProps {
  store: ITaskStore;
  onClose?: () => void;
  onTaskUpdate?: (taskId: string, changes: Partial<TaskSnapshot>) => void;
  onPredecessorUpdate?: (taskId: string, change: PredecessorLinkChange) => void;
  resolvePredecessorWbsCode?: (taskId: string) => string | null;
  onTrackTask?: (taskId: string) => void | Promise<void>;
  trackTaskLoadingTaskId?: string | null;
  /** Switch task details layout to the bottom panel. */
  onSwitchToBottomPanel?: () => void;
  /** When compare mode is on, show Current vs Baseline N in the form. */
  compareBaselineIndex?: number | null;
  /** Same gate as Gantt "Save to Server". */
  canMutate?: boolean;
  /** Increment to scroll Issues / Hindrances into view after open. */
  scrollToLinkedIssuesRequest?: number;
  /** Open predecessor task-tree picker for the selected task. */
  onPickPredecessors?: () => void;
  /** Open Assign users drawer for the selected task. */
  onOpenAssignUsers?: () => void;
  /** Assignees summary line for the selected task. */
  assigneesSummary?: string;
  /** Register draft mutators so GanttPage picker can update drawer predecessors. */
  registerPredecessorDraftMutator?: (
    mutator: { getDraft: () => string; applyDraft: (value: string) => void } | null,
  ) => void;
}

export default function TaskDetailsDrawer({
  store,
  onClose,
  onTaskUpdate,
  onPredecessorUpdate,
  resolvePredecessorWbsCode,
  onTrackTask,
  trackTaskLoadingTaskId,
  onSwitchToBottomPanel,
  compareBaselineIndex = null,
  canMutate = true,
  scrollToLinkedIssuesRequest = 0,
  onPickPredecessors,
  onOpenAssignUsers,
  assigneesSummary,
  registerPredecessorDraftMutator,
}: TaskDetailsDrawerProps) {
  const handleSaveSuccess = useCallback(() => {
    store.setSelectedTaskId(null);
    onClose?.();
  }, [store, onClose]);

  const {
    selectedTaskId,
    draft,
    isDirty,
    isSaving,
    readOnlyForNonLeaf,
    resolvedLocationValue,
    draftPredecessors,
    successorText,
    predecessorFieldLabel,
    successorFieldLabel,
    isLeafTask,
    updateDraftField,
    updateDraftQuantity,
    quantityErrors,
    quantityDirty,
    updateDraftPredecessors,
    commitDraftPredecessors,
    saveChanges,
    cancelChanges,
  } = useTaskDetailsDraft(store, {
    onApplyChanges: onTaskUpdate,
    onCommitPredecessorLinks: onPredecessorUpdate,
    resolvePredecessorWbsCode,
    onSaveSuccess: handleSaveSuccess,
  });

  useEffect(() => {
    if (!registerPredecessorDraftMutator) return;
    registerPredecessorDraftMutator({
      getDraft: () => draftPredecessors,
      applyDraft: (value: string) => {
        updateDraftPredecessors(value);
        commitDraftPredecessors(value);
      },
    });
    return () => registerPredecessorDraftMutator(null);
  }, [
    registerPredecessorDraftMutator,
    draftPredecessors,
    updateDraftPredecessors,
    commitDraftPredecessors,
  ]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      cancelChanges();
    }
    store.setSelectedTaskId(null);
    onClose?.();
  }, [store, onClose, isDirty, isSaving, cancelChanges]);

  const showTrackTask = useMemo(() => {
    if (!draft || !onTrackTask || !isLeafTask) return false;
    const rows = draft.unitActivityDetails;
    return !(Array.isArray(rows) && rows.length > 0);
  }, [draft, onTrackTask, isLeafTask]);

  const trackTaskLoading =
    draft != null &&
    trackTaskLoadingTaskId != null &&
    String(trackTaskLoadingTaskId) === String(draft.id);

  return (
    <Drawer
      opened={!!draft}
      onClose={handleClose}
      transitionProps={{ duration: 200 }}
      title={
        <Group gap={8} justify="space-between" wrap="nowrap" style={{ width: '100%', minWidth: 0 }}>
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fw={600}>Task Details</Text>
            {showTrackTask ? (
              <TrackTaskConfirmControl
                canMutate={canMutate}
                loading={trackTaskLoading}
                disabled={isSaving}
                onConfirm={() => {
                  if (!draft || !onTrackTask || !canMutate) return;
                  if (quantityDirty) {
                    notifications.show({
                      title: 'Save changes first',
                      message: 'Save UOM and quantity changes before tracking this task.',
                      color: 'yellow',
                    });
                    return;
                  }
                  void onTrackTask(draft.id);
                }}
              />
            ) : null}
            {draft?.critical ? (
              <Group gap={4} wrap="nowrap">
                <IconAlertTriangle size={13} color="var(--mantine-color-red-6)" />
                <Text size="xs" c="red" fw={600}>
                  Critical task
                </Text>
              </Group>
            ) : null}
          </Group>
          <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
            {onSwitchToBottomPanel ? (
              <Button
                size="xs"
                variant="subtle"
                disabled={isSaving}
                onClick={onSwitchToBottomPanel}
              >
                Switch to Bottom panel
              </Button>
            ) : null}
          </Group>
        </Group>
      }
      position="right"
      size={560}
      withOverlay={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={false}
      closeOnEscape={!isSaving}
      classNames={shellDrawerClassNames}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: DRAWER_SURFACE,
          boxShadow:
            '-18px 0 36px rgba(15, 23, 42, 0.18), -4px 0 10px rgba(15, 23, 42, 0.12)',
          borderLeft: '1px solid var(--mantine-color-default-border)',
        },
        header: {
          flexShrink: 0,
          backgroundColor: DRAWER_SURFACE,
        },
        title: {
          flex: 1,
          marginRight: 8,
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: DRAWER_SURFACE,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      {!draft ? null : (
        <TaskModalBusyOverlay
          busy={isSaving}
          label="Saving changes.."
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: 'var(--mantine-spacing-md)',
              }}
            >
              <TaskDetailsFormBody
                taskIdDisplay={selectedTaskId ?? draft.id}
                draft={draft}
                readOnlyForNonLeaf={readOnlyForNonLeaf}
                resolvedLocationValue={resolvedLocationValue}
                draftPredecessors={draftPredecessors}
                successorText={successorText}
                predecessorFieldLabel={predecessorFieldLabel}
                successorFieldLabel={successorFieldLabel}
                updateDraftField={updateDraftField}
                updateDraftQuantity={updateDraftQuantity}
                quantityErrors={quantityErrors}
                getTask={(id) => store.getTask(id)}
                updateDraftPredecessors={updateDraftPredecessors}
                commitDraftPredecessors={commitDraftPredecessors}
                compareBaselineIndex={compareBaselineIndex}
                scrollToLinkedIssuesRequest={scrollToLinkedIssuesRequest}
                onPickPredecessors={onPickPredecessors}
                onOpenAssignUsers={onOpenAssignUsers}
                assigneesSummary={assigneesSummary}
              />
            </Box>
            <TaskDetailsFormActions
              isDirty={isDirty}
              saving={isSaving}
              onSave={saveChanges}
              onCancel={cancelChanges}
              surfaceBackground={DRAWER_SURFACE}
              canMutate={canMutate}
            />
          </Box>
        </TaskModalBusyOverlay>
      )}
    </Drawer>
  );
}
